import { datadogRum } from '@datadog/browser-rum';
import { hasStatisticsConsent } from 'utils/helpers/cookiebot';

/*
Datadog RUM is a "statistics" cookie. It is never initialised on page load —
utils/analyticsConsent owns the lifecycle: it enables RUM once Cookiebot reports
statistics consent and disables it again if that consent is withdrawn.
*/

// Once `init` has run the SDK stays loaded for the life of the page, so a
// re-grant has to resume it rather than initialise it a second time.
let hasInitialized = false;

/*
Only needed to switch tracking off. Feature-detected so an older RUM build (or
a test double) that lacks them degrades to "no further calls" instead of throwing.
*/
const setRumTrackingConsent = (trackingConsent: 'granted' | 'not-granted') => {
  if (typeof datadogRum.setTrackingConsent === 'function') {
    datadogRum.setTrackingConsent(trackingConsent);
  }
};

const stopRumSession = () => {
  if (typeof datadogRum.stopSession === 'function') {
    datadogRum.stopSession();
  }
};

export const initDatadogMinimal = () => {
  if (!hasStatisticsConsent()) {
    // Statistics cookies not accepted: do not load or contact Datadog at all.
    return false;
  }

  console.log('Initializing Datadog RUM (minimal setup)...');

  try {
    // Check if already initialized
    try {
      if (datadogRum.getInternalContext && datadogRum.getInternalContext()) {
        console.log('Datadog already initialized');
        hasInitialized = true;
        return;
      }
    } catch (e) {
      // Continue with initialization
    }

    const getSessionReplaySampleRate = () => {
      const env = process.env.REACT_APP_ENV;
      if (env === 'uat') {
        return 40;
      }
      if (env === 'prod') {
        return 50;
      }
      return 40; // Default for other environments
    };

    datadogRum.init({
      applicationId: '39da6fa4-88ca-42ff-b8b8-80db9905920c',
      clientToken: 'pub35f1c144784a8ea599e7758b5110a947',
      site: 'datadoghq.eu' as const,
      service: 'web-react-online-seed-recommendation',
      env: process.env.REACT_APP_ENV,
      // Set at build time from CIRCLE_TAG (see .circleci/config.yml's "Build
      // application" step) so Datadog DORA can match RUM sessions to the prod
      // deploy that shipped them. Falls back to the old hard-coded value when
      // unset (e.g. local dev, or a build that predates this env var).
      version: process.env.REACT_APP_DD_VERSION || '1.0.0',
      sessionSampleRate: 100,
      sessionReplaySampleRate: getSessionReplaySampleRate(),
      // We only get here with consent, but keep the SDK's own consent model in
      // sync so `setTrackingConsent` below is the single off-switch.
      trackingConsent: 'granted',
    });

    hasInitialized = true;
    console.log('Datadog RUM initialized with minimal config');

    return true;
  } catch (error) {
    console.error('Failed to initialize Datadog:', error);
    return false;
  }
};

export const enableDatadogTracking = (): boolean => {
  if (!hasStatisticsConsent()) {
    return false;
  }
  if (hasInitialized) {
    // Re-granted after a revoke: the SDK is already loaded, just resume it.
    setRumTrackingConsent('granted');
    return true;
  }
  return initDatadogMinimal() !== false;
};

export const disableDatadogTracking = (): boolean => {
  if (!hasInitialized) {
    // Never initialised, so nothing is sending data.
    return false;
  }
  setRumTrackingConsent('not-granted');
  stopRumSession();
  return true;
};
